import React, { useState, useCallback } from 'react';
import { Box, Modal, IconButton } from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import { MessageProps } from '../../../types/chat';
import MessageBubble from './MessageBubble';

const ImageMessage: React.FC<MessageProps> = React.memo(({ 
  message, 
  isCurrentUser,
  onImagePress,
  onReactionPress,
  onReplyPress,
  onDeletePress,
  currentUserId,
  currentUserType
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleImageClick = useCallback(() => {
    setModalOpen(true);
    if (message.imageUrl) {
      onImagePress?.(message.imageUrl, message);
    }
  }, [message, onImagePress]);

  const handleReactionPress = (emoji: string) => {
    onReactionPress?.(message, emoji);
  };

  const handleDownload = useCallback(async () => {
    if (!message.imageUrl) return;
    
    try {
      const response = await fetch(message.imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `image-${message.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download image:', error);
    }
  }, [message.imageUrl, message.id]);

  if (!message.imageUrl) {
    return (
      <MessageBubble 
        message={message} 
        isCurrentUser={isCurrentUser}
        onReactionPress={handleReactionPress}
        onReplyPress={onReplyPress}
        onDeletePress={onDeletePress}
        currentUserId={currentUserId}
        currentUserType={currentUserType}
      >
        <Box sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          Image not available
        </Box>
      </MessageBubble>
    );
  }

  return (
    <>
      <MessageBubble 
        message={message} 
        isCurrentUser={isCurrentUser}
        onReactionPress={handleReactionPress}
        onReplyPress={onReplyPress}
        onDeletePress={onDeletePress}
        onImagePress={onImagePress}
        currentUserId={currentUserId}
        currentUserType={currentUserType}
      >
        <Box sx={{ position: 'relative' }}>
          <Box
            component="img"
            src={message.imageUrl}
            alt="Shared image"
            onClick={handleImageClick}
            sx={{
              maxWidth: 300,
              maxHeight: 200,
              width: '100%',
              height: 'auto',
              borderRadius: 1,
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              display: 'block',
              '&:hover': { 
                opacity: 0.9,
                transform: 'scale(1.02)'
              }
            }}
          />
          
          {/* Optional message text with image */}
          {message.message && message.message.trim() && (
            <Box sx={{ mt: 1, fontSize: '14px', lineHeight: 1.4 }}>
              {message.message}
            </Box>
          )}
        </Box>
      </MessageBubble>

      {/* Full-screen image modal */}
      <Modal 
        open={modalOpen} 
        onClose={() => setModalOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box sx={{ 
          position: 'relative',
          maxWidth: '90vw',
          maxHeight: '90vh',
          outline: 'none',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'black',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
        }}>
          {/* Close button */}
          <IconButton
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {/* Download button */}
          <IconButton
            onClick={handleDownload}
            sx={{
              position: 'absolute',
              top: 8,
              right: 56,
              zIndex: 1,
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              }
            }}
          >
            <DownloadIcon />
          </IconButton>

          {/* Full-size image */}
          <Box
            component="img"
            src={message.imageUrl}
            alt="Full size image"
            sx={{ 
              width: '100%', 
              height: 'auto',
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              display: 'block'
            }} 
          />
        </Box>
      </Modal>
    </>
  );
});

ImageMessage.displayName = 'ImageMessage';

export default ImageMessage;
